/**
 * Trade Ideas Table — filterable, sortable trade ideas
 */
import React, { useState, useEffect } from 'react';
import {
  Box, Typography, Table, TableBody, TableCell, TableContainer,
  TableHead, TableRow, TablePagination, IconButton, Tooltip, alpha, Skeleton,
} from '@mui/material';
import { Download, Refresh, Lightbulb } from '@mui/icons-material';
import { useTranslation } from 'react-i18next';
import { tradingDecisionAPI } from '../../services/api';
import TradeIdeasFilters from './TradeIdeasFilters';
import TradeIdeasTableRow from './TradeIdeasTableRow';
import { CACHE_DURATION, formatPrice, normalizeSectorField, translateSector, handleCSVExport } from './tradeIdeasUtils';

const EmptyState = ({ msg }) => (
  <Box sx={{ py: 4, textAlign: 'center' }}>
    <Box sx={{ width: 48, height: 48, borderRadius: '50%', border: '2px dashed #e2e8f0', mx: 'auto', mb: 1.5, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
      <Lightbulb sx={{ fontSize: 20, color: '#cbd5e1' }} />
    </Box>
    <Typography sx={{ fontSize: '0.82rem', fontWeight: 600, color: '#94a3b8' }}>{msg}</Typography>
  </Box>
);

const TableSkeleton = () => (
  <Box sx={{ borderRadius: 2, border: '1px solid #e2e8f0', bgcolor: '#fff', overflow: 'hidden' }}>
    <Box sx={{ px: 2, py: 1.2, borderBottom: '1px solid #f1f5f9', display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
        <Skeleton variant="rounded" width={28} height={28} sx={{ borderRadius: 1.5 }} />
        <Skeleton width={120} height={18} />
      </Box>
      <Box sx={{ display: 'flex', gap: 0.8 }}>
        <Skeleton variant="circular" width={28} height={28} />
        <Skeleton variant="circular" width={28} height={28} />
      </Box>
    </Box>
    <Box sx={{ px: 2, py: 1, display: 'flex', gap: 1 }}>
      {[0,1,2,3].map(i => <Skeleton key={i} variant="rounded" width={140} height={36} sx={{ borderRadius: 1.5 }} />)}
    </Box>
    <Box sx={{ px: 2, pb: 2 }}>
      {/* header */}
      <Box sx={{ display: 'grid', gridTemplateColumns: 'repeat(10, 1fr)', gap: 1, py: 0.8, borderBottom: '1px solid #f1f5f9' }}>
        {Array(10).fill(0).map((_, i) => <Skeleton key={i} height={12} />)}
      </Box>
      {/* rows */}
      {[0,1,2,3,4,5,6,7,8,9].map(r => (
        <Box key={r} sx={{ display: 'grid', gridTemplateColumns: 'repeat(10, 1fr)', gap: 1, py: 0.6 }}>
          {Array(10).fill(0).map((_, i) => <Skeleton key={i} height={14} />)}
        </Box>
      ))}
    </Box>
  </Box>
);

const TradeIdeasTable = ({ calcDate = null, nav = 1000000000, onRefresh = null }) => {
  const { t, i18n } = useTranslation();
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [ideas, setIdeas] = useState([]);
  const [riskFilter, setRiskFilter] = useState('normal');
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(10);
  const [filterSector, setFilterSector] = useState('');
  const [filterSetup, setFilterSetup] = useState('');
  const [sortBy, setSortBy] = useState('risk_pct');
  const [sortOrder, setSortOrder] = useState('asc');
  const [lastFetch, setLastFetch] = useState(null);

  useEffect(() => { fetchIdeas(); }, [calcDate, nav]); // eslint-disable-line

  const fetchIdeas = async (force = false) => {
    try {
      setLoading(true); setError(null);
      const now = Date.now();
      const shouldForce = force || !lastFetch || now - lastFetch > CACHE_DURATION;
      const r = await tradingDecisionAPI.getTradeIdeas(calcDate, nav, 100, !shouldForce);
      const raw = r.data || [];
      const seen = new Set();
      const unique = raw.map(i => ({ ...i, sector: normalizeSectorField(i) })).filter(i => {
        const k = `${i.ticker}-${i.date || i.entry_date}`;
        if (seen.has(k)) return false; seen.add(k); return true;
      });
      setIdeas(unique); setLastFetch(Date.now());
      if (onRefresh) onRefresh(unique.length);
    } catch (e) { setError(e.message || 'Failed'); }
    finally { setLoading(false); }
  };

  const sectors = [...new Set(ideas.map(i => translateSector(normalizeSectorField(i), i18n.language)).filter(Boolean))].sort();
  const getIdeaSector = i => translateSector(normalizeSectorField(i), i18n.language);

  const filtered = ideas
    .filter(i => {
      if (riskFilter === 'normal' && i.is_high_risk) return false;
      if (riskFilter === 'high-risk' && !i.is_high_risk) return false;
      if (filterSector && getIdeaSector(i) !== filterSector) return false;
      if (filterSetup && i.reason !== filterSetup) return false;
      return true;
    })
    .sort((a, b) => {
      const aV = a[sortBy] || 0, bV = b[sortBy] || 0;
      return sortOrder === 'asc' ? aV - bV : bV - aV;
    });

  const handleSort = f => { if (sortBy === f) setSortOrder(o => o === 'asc' ? 'desc' : 'asc'); else { setSortBy(f); setSortOrder('asc'); } };

  const csvData = filtered.map(i => ({
    Ticker: i.ticker, Date: i.date?.split('T')[0] || 'N/A',
    Entry: formatPrice(i.entry), Stop: formatPrice(i.stop),
    'Risk %': `${(i.risk_pct * 100).toFixed(2)}%`, 'Size %': `${(i.size_nav_frac * 100).toFixed(2)}%`,
    Reason: i.reason, RS: i.rs?.toFixed(2) || 'N/A', MoneyFlow: i.money_flow ? formatPrice(i.money_flow) : 'N/A',
    Sector: getIdeaSector(i) || 'N/A',
  }));

  if (loading) return <TableSkeleton />;

  if (error) {
    return (
      <Box sx={{ borderRadius: 2, border: '1px solid #e2e8f0', bgcolor: '#fff', p: 2 }}>
        <Typography sx={{ fontSize: '0.8rem', color: '#ef4444' }}>{error}</Typography>
      </Box>
    );
  }

  return (
    <Box sx={{ borderRadius: 2, border: '1px solid #e2e8f0', bgcolor: '#fff', overflow: 'hidden' }}>
      {/* Header */}
      <Box sx={{ px: 2, py: 1.2, display: 'flex', justifyContent: 'space-between', alignItems: 'center', borderBottom: '1px solid #f1f5f9' }}>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
          <Box sx={{ width: 28, height: 28, borderRadius: 1.5, background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
            <Lightbulb sx={{ fontSize: 16, color: '#fff' }} />
          </Box>
          <Typography sx={{ fontSize: '0.88rem', fontWeight: 700, color: '#1e293b' }}>
            {t('tradingDecision.tradeIdeasTable.title', 'Trade Ideas')}
          </Typography>
          <Box sx={{ px: 0.6, py: 0.15, borderRadius: 1, bgcolor: alpha('#667eea', 0.1) }}>
            <Typography sx={{ fontSize: '0.7rem', fontWeight: 700, color: '#667eea' }}>{filtered.length}</Typography>
          </Box>
        </Box>
        <Box sx={{ display: 'flex', gap: 0.5 }}>
          <Tooltip title={t('tradingDecision.tradeIdeasTable.refresh', 'Refresh')}>
            <IconButton onClick={() => fetchIdeas(true)} size="small" sx={{ width: 30, height: 30, bgcolor: alpha('#667eea', 0.06), '&:hover': { bgcolor: alpha('#667eea', 0.12) } }}>
              <Refresh sx={{ fontSize: 16 }} />
            </IconButton>
          </Tooltip>
          {csvData.length > 0 && (
            <Tooltip title={t('tradingDecision.tradeIdeasTable.export', 'Export CSV')}>
              <IconButton onClick={() => handleCSVExport(csvData)} size="small" sx={{ width: 30, height: 30, bgcolor: alpha('#667eea', 0.06), '&:hover': { bgcolor: alpha('#667eea', 0.12) } }}>
                <Download sx={{ fontSize: 16 }} />
              </IconButton>
            </Tooltip>
          )}
        </Box>
      </Box>

      {/* High risk warning */}
      {riskFilter === 'high-risk' && (
        <Box sx={{ mx: 2, mt: 1, px: 1.5, py: 0.6, borderRadius: 1, bgcolor: alpha('#f59e0b', 0.06), border: `1px solid ${alpha('#f59e0b', 0.15)}` }}>
          <Typography sx={{ fontSize: '0.72rem', color: '#92400e' }}>
            <Typography component="span" sx={{ fontWeight: 700, fontSize: '0.72rem' }}>{t('tradingDecision.tradeIdeasTable.highRiskAlert', 'High Risk')}</Typography>: {t('tradingDecision.tradeIdeasTable.highRiskWarning', 'Use smaller position sizes')}
          </Typography>
        </Box>
      )}


      {/* Filters */}
      <Box sx={{ px: 2, py: 1, display: 'flex', gap: 1.5, flexWrap: 'wrap', alignItems: 'center', borderBottom: '1px solid #f1f5f9' }}>
        <TradeIdeasFilters riskFilter={riskFilter} setRiskFilter={setRiskFilter} filterSector={filterSector}
          setFilterSector={setFilterSector} filterSetup={filterSetup} setFilterSetup={setFilterSetup}
          sortBy={sortBy} setSortBy={setSortBy} sectors={sectors} setPage={setPage} />
      </Box>

      {/* Table or empty */}
      {filtered.length === 0 ? (
        <EmptyState msg={t('tradingDecision.tradeIdeasTable.noIdeas', 'No trade ideas found')} />
      ) : (
        <>
          <TableContainer>
            <Table size="small">
              <TableHead>
                <TableRow>
                  <TableCell sx={{ fontWeight: 700, fontSize: '0.68rem', color: '#64748b', py: 0.6, px: 1, borderColor: '#f1f5f9' }}>{t('tradingDecision.tradeIdeasTable.columns.ticker', 'Ticker')}</TableCell>
                  <TableCell sx={{ fontWeight: 700, fontSize: '0.68rem', color: '#64748b', py: 0.6, px: 1, borderColor: '#f1f5f9' }}>{t('tradingDecision.tradeIdeasTable.columns.date', 'Date')}</TableCell>
                  <TableCell align="right" sx={{ fontWeight: 700, fontSize: '0.68rem', color: '#64748b', py: 0.6, px: 1, borderColor: '#f1f5f9' }}>{t('tradingDecision.tradeIdeasTable.columns.entryPrice', 'Entry')}</TableCell>
                  <TableCell align="right" sx={{ fontWeight: 700, fontSize: '0.68rem', color: '#64748b', py: 0.6, px: 1, borderColor: '#f1f5f9' }}>{t('tradingDecision.tradeIdeasTable.columns.stopPrice', 'Stop')}</TableCell>
                  <TableCell align="right" sx={{ fontWeight: 700, fontSize: '0.68rem', color: '#64748b', py: 0.6, px: 1, cursor: 'pointer', borderColor: '#f1f5f9' }} onClick={() => handleSort('risk_pct')}>
                    {t('tradingDecision.tradeIdeasTable.columns.riskPct', 'Risk%')}{sortBy === 'risk_pct' && (sortOrder === 'asc' ? ' ↑' : ' ↓')}
                  </TableCell>
                  <TableCell align="right" sx={{ fontWeight: 700, fontSize: '0.68rem', color: '#64748b', py: 0.6, px: 1, cursor: 'pointer', borderColor: '#f1f5f9' }} onClick={() => handleSort('size_nav_frac')}>
                    {t('tradingDecision.tradeIdeasTable.columns.sizeNavFrac', 'Size%')}{sortBy === 'size_nav_frac' && (sortOrder === 'asc' ? ' ↑' : ' ↓')}
                  </TableCell>
                  <TableCell sx={{ fontWeight: 700, fontSize: '0.68rem', color: '#64748b', py: 0.6, px: 1, borderColor: '#f1f5f9' }}>{t('tradingDecision.tradeIdeasTable.columns.setup', 'Setup')}</TableCell>
                  <TableCell align="right" sx={{ fontWeight: 700, fontSize: '0.68rem', color: '#64748b', py: 0.6, px: 1, cursor: 'pointer', borderColor: '#f1f5f9' }} onClick={() => handleSort('rs')}>
                    {t('tradingDecision.tradeIdeasTable.columns.rs', 'RS')}{sortBy === 'rs' && (sortOrder === 'asc' ? ' ↑' : ' ↓')}
                  </TableCell>
                  <TableCell align="right" sx={{ fontWeight: 700, fontSize: '0.68rem', color: '#64748b', py: 0.6, px: 1, borderColor: '#f1f5f9' }}>{t('tradingDecision.tradeIdeasTable.columns.moneyFlow', 'Flow')}</TableCell>
                  <TableCell sx={{ fontWeight: 700, fontSize: '0.68rem', color: '#64748b', py: 0.6, px: 1, borderColor: '#f1f5f9' }}>{t('tradingDecision.tradeIdeasTable.columns.sector', 'Sector')}</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {filtered.slice(page * rowsPerPage, page * rowsPerPage + rowsPerPage).map((idea, i) => (
                  <TradeIdeasTableRow key={i} idea={idea} getIdeaSector={getIdeaSector} />
                ))}
              </TableBody>
            </Table>
          </TableContainer>
          <TablePagination component="div" count={filtered.length} page={page}
            onPageChange={(_, p) => setPage(p)} rowsPerPage={rowsPerPage}
            onRowsPerPageChange={e => { setRowsPerPage(parseInt(e.target.value, 10)); setPage(0); }}
            rowsPerPageOptions={[10, 25, 50, 100]}
            labelRowsPerPage={t('tradingDecision.tradeIdeasTable.rowsPerPage', 'Rows')}
            sx={{ borderTop: '1px solid #f1f5f9', '& .MuiTablePagination-toolbar': { minHeight: 40 } }} />
        </>
      )}
    </Box>
  );
};

export default TradeIdeasTable;
